import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button, type SegmentedTabItem } from '@/ui';
import { totalAmount, totalOf, unitRate } from '@/lib/money';
import { marketplaceKeys } from '@/lib/task-query-keys';
import { filtersToServerParams } from '@/lib/task-list-url';
import { useServerList } from '@/lib/task-server-list';
import { pairOf } from '@/lib/task-currency';
import { useSessionStore } from '@/auth/session.store';
import { HowItWorks } from '@/features/help/HowItWorks';
import { hasPermission } from '@/auth/types';
import { fetchTaskPage } from './api';
import {
  MARKET_TABS as TABS,
  marketTabParams as tabParams,
  type MarketTab as Tab,
} from './tab-params';
import { counterpartyName, type TaskRow } from './types';
import { marketOutcomeView } from './market-outcome';
import { offerLastWriterName, offerOwnership } from './offer-ownership';
import { useUrlList } from './useUrlList';
import { ListScreen } from './components/ListScreen';
import {
  BankPanel,
  InfoGrid,
  QuoteActions,
  QuoteCard,
  QuoteHeader,
  QuoteNote,
  type InfoItem,
} from './components/QuoteCard';
import { useCountdown } from './components/useCountdown';

/**
 * PIYASA — buronun gordugu TALEP listesi.
 *
 * Iki sekme: "Acik" teklif toplama + karar fazindaki talepler (offer,decide —
 * yalniz offer fazinda teklif verilebilir, decide fazinda satir salt okunur),
 * "Gecmis" ise artik ACIK OLMAYAN her talep (2026-10-02, `marketTab=history`): kapanmislar VE
 * karara baglanmis ama suren isler. Kart sunucunun `marketOutcome`'unu rozet olarak gosterir
 * (Kazandiniz / Buronuz kazandi / Baska buroya verildi / Kapandi / Iptal edildi / Suresi doldu,
 * ./market-outcome.ts). Eskiden Gecmis yalniz kapanmis isleri gosteriyordu ve karara baglanan
 * talep Piyasa'nin hicbir sekmesinde kalmiyordu. Ilk surumde tek sekme vardi ve yalniz
 * `offer` fazini cekiyordu; talep olusturulduktan 2 dakika sonra listeden
 * kayboluyordu ve buro "talep hic gelmedi" saniyordu. "Onaylanan" ayrimi buro
 * icin hala kendi TEKLIFLERI uzerinden anlamli (bkz. Tekliflerim).
 *
 * Sekme -> sunucu parametresi eslemesi ./tab-params.ts'te.
 *
 * `taskPretenderCurrencyUserId` sahiplik degil ESLESME parametresi: sunucu
 * buronun ilgilendigi para birimlerine gore filtreliyor ve bu gorunumde satira
 * BURONUN teklifini de ekliyor (`offerPrice` / `offerUserId`; 2026-10-03'ten beri cagiranin
 * kendisi oncelikli, yoksa is arkadasinin). Bu yuzden "buro zaten teklif verdi" bilgisi ayri bir
 * istek gerektirmiyor; kimin teklifi oldugu ./offer-ownership.ts'te.
 */

const DEFAULT_TAB: Tab = 'openRequests';

const isTab = (value: string): value is Tab => (TABS as readonly string[]).includes(value);

const SORT_OPTIONS = [
  { value: 'taskCreatedAt:desc', labelKey: 'filter.sortNewest' },
  { value: 'taskCreatedAt:asc', labelKey: 'filter.sortOldest' },
  { value: 'taskAmount:desc', labelKey: 'filter.sortAmountDesc' },
  { value: 'taskAmount:asc', labelKey: 'filter.sortAmountAsc' },
] as const;

export function MarketPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const snapshot = useSessionStore((state) => state.snapshot);
  const userId = snapshot?.user.id;
  const canOffer = hasPermission(snapshot, 'createOffer');
  // Is arkadasinin teklifini guncellemek (bir buro bir teklif, 2026-10-03).
  const canEditOffer = hasPermission(snapshot, 'editOffer');

  const url = useUrlList(DEFAULT_TAB);
  const tab: Tab = isTab(url.tab) ? url.tab : DEFAULT_TAB;

  const params = useMemo(
    () =>
      userId
        ? {
            taskPretenderCurrencyUserId: userId,
            ...tabParams(tab),
            ...filtersToServerParams(url.filters),
          }
        : {},
    [userId, tab, url.filters],
  );

  const list = useServerList<TaskRow>({
    baseKey: marketplaceKeys.taskLists(),
    params,
    fetchPage: fetchTaskPage,
    offset: url.offset,
    onParamsChanged: url.resetOffset,
    enabled: Boolean(userId),
    // Web'de push yok: yeni talep 2 dk'lik teklif penceresinde gorunmeli (lib/task-server-list.ts).
    pollMs: tab === 'openRequests' ? 10_000 : undefined,
  });

  const renderRow = useCallback(
    (task: TaskRow) => (
      <MarketCard
        task={task}
        receivedAtMs={list.receivedAtMs}
        userId={userId}
        canOffer={canOffer}
        canEditOffer={canEditOffer}
        onOpen={() => {
          void navigate(`/buro/piyasa/${task.id}`);
        }}
      />
    ),
    [list.receivedAtMs, userId, canOffer, canEditOffer, navigate],
  );

  const tabs: SegmentedTabItem[] = [
    { value: 'openRequests', label: t('task.tabs.openRequests') },
    { value: 'pastRequests', label: t('task.tabs.past') },
  ];

  return (
    <ListScreen
      // "Piyasa" (2026-10-02): "Doviz Talepleri" musterinin Taleplerim basligiyla ayniydi.
      title={t('task.marketTitle')}
      header={<HowItWorks audience="exchange" />}
      tabs={tabs}
      tab={tab}
      onTabChange={url.setTab}
      filters={url.filters}
      onFiltersChange={url.setFilters}
      sortOptions={SORT_OPTIONS}
      list={list}
      onOffsetChange={url.setOffset}
      onClearFilters={() => {
        url.setFilters({
          ...url.filters,
          q: '',
          currencies: [],
          createdFrom: '',
          createdTo: '',
          amountMin: '',
          amountMax: '',
        });
      }}
      rowKey={(task) => task.id}
      renderRow={renderRow}
    />
  );
}

type MarketCardProps = {
  task: TaskRow;
  receivedAtMs: number;
  /** Oturumdaki kullanici: satirdaki teklif onun mu, is arkadasinin mi. */
  userId: string | undefined;
  canOffer: boolean;
  canEditOffer: boolean;
  onOpen: () => void;
};

/**
 * Buronun gordugu talep karti.
 *
 * Musteri kartindan farki: karsi taraf ve (varsa) BURONUN teklifi dogrudan
 * gorunur — buro bunlara bakarak fiyat veriyor. Banka detayi KATLI durur;
 * takas anina kadar gerekmiyor ve listenin taranabilirligini bozuyor.
 *
 * Teklif is arkadasininsa (2026-10-03, bir buro bir teklif) etiket "Buronuzun teklifi", altinda
 * "Son guncelleyen"; dugme "Buronun teklifini guncelle" ve `editOffer` ister. Yetki yoksa dugme
 * yerine not: yeni teklif verilemez, sunucu 403 ORGANIZATION_OFFER_EXISTS doner. Kendi teklifinde
 * dugme `editOffer`, teklif yokken `createOffer` ister (detay sayfasiyla ayni kural).
 */
function MarketCard({
  task,
  receivedAtMs,
  userId,
  canOffer,
  canEditOffer,
  onOpen,
}: MarketCardProps) {
  const { t } = useTranslation();
  // Karar sonucu (Gecmis): rozet + kazanan cercevesi / kaybeden solgunlugu (OfferCard ile ayni).
  const outcome = marketOutcomeView(task.marketOutcome);
  const phase = useCountdown({
    taskStatusCode: task.taskStatusCode,
    // Secilmeyen buro icin musterinin odeme sayaci ilgisiz: kart kapali cizilir (M1).
    countdownStatus: outcome?.closesPhase ? 'passive' : task.countdownStatus,
    statusUpdatedAt: task.statusUpdatedAt,
    countdown: task.countdown,
    receivedAtMs,
  });

  const pair = pairOf(task.taskTypeCode);
  const ownership = offerOwnership(task, userId);
  const alreadyBid = ownership !== 'none';
  const colleagueOffer = ownership === 'colleague';
  const lastWriter = offerLastWriterName(task, userId);
  const name = counterpartyName(task.userBusinessName, task.userFullName);

  const info: InfoItem[] = [];
  if (task.offerPrice !== null) {
    const rate = unitRate(task.offerPrice);
    info.push({
      label: colleagueOffer ? t('task.orgOfferPrice') : t('labels.price'),
      rate,
      currency: pair.secondCurrency,
    });
    info.push({
      label: t('labels.total'),
      total: totalOf(rate, task.taskAmount),
      currency: pair.secondCurrency,
      emphasis: true,
    });
  }
  if (lastWriter !== null) info.push({ label: t('task.offerLastUpdatedBy'), text: lastWriter });
  info.push({
    label: task.userBusinessName ? t('labels.company') : t('labels.name'),
    text: name ?? '',
  });

  return (
    <QuoteCard phase={phase} won={outcome?.won ?? false} lost={outcome?.lost ?? false}>
      <QuoteHeader
        id={task.id}
        taskTypeCode={task.taskTypeCode}
        isBuying={task.taskIsBuying}
        amount={totalAmount(task.taskAmount)}
        amountCurrency={pair.currency}
        createdAt={task.createdAt}
        phase={phase}
        badgeLabel={outcome ? t(outcome.labelKey) : undefined}
      />

      <InfoGrid items={info} />

      <BankPanel account={task.taskData?.selectedAccount} />

      {/* Teklif yalniz TEKLIF TOPLAMA fazinda verilebilir; sonrasinda dugme hic
          cizilmez — calismayacak bir dugme, hata mesajindan daha kotudur. */}
      {phase.phase !== 'offer' ? null : colleagueOffer ? (
        canEditOffer ? (
          <QuoteActions>
            <Button size="sm" onClick={onOpen}>
              {t('offerFlow.updateOrgOffer')}
            </Button>
          </QuoteActions>
        ) : (
          <QuoteNote>{t('errors.organizationOfferExists')}</QuoteNote>
        )
      ) : (alreadyBid ? canEditOffer : canOffer) ? (
        // Detay sayfasiyla AYNI kural: var olan teklif editOffer, yeni teklif createOffer ister.
        // Eskiden kendi teklifinde de createOffer'a bakiyordu; dugme editOffer'i olmayana
        // "Teklifi guncelle" gosterip detayda yetki notuna dusuruyordu (2026-10-03).
        <QuoteActions>
          <Button size="sm" onClick={onOpen}>
            {alreadyBid ? t('offerFlow.update') : t('offerFlow.create')}
          </Button>
        </QuoteActions>
      ) : null}
    </QuoteCard>
  );
}
